import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, X, FloppyDisk, Star } from "@phosphor-icons/react";

const field = "w-full rounded-md border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-teal/30";
const EMPTY = { name: "", role: "", country: "", quote: "", rating: 5, order: 100 };

function TestimonialCard({ t, onEdit, onDelete }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-testimonial-${t.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-heading text-sm font-semibold text-ink">{t.name}</p>
          <p className="text-xs text-slate-400">{t.role}{t.country ? ` · ${t.country}` : ""}</p>
          <div className="mt-1 flex gap-0.5">{Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={`star-${i}`} size={12} weight="fill" className="text-amber-400" />)}</div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => onEdit(t)} data-testid={`edit-testimonial-${t.id}`} className="text-slate-400 hover:text-teal"><PencilSimple size={18} /></button>
          <button onClick={() => onDelete(t.id)} data-testid={`delete-testimonial-${t.id}`} className="text-slate-400 hover:text-red-600"><Trash size={18} /></button>
        </div>
      </div>
      <p className="mt-2 line-clamp-2 text-xs text-slate-600">“{t.quote}”</p>
    </div>
  );
}

export function TestimonialsManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(EMPTY); setEditId(null); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, rating: Number(form.rating), order: Number(form.order) };
    try {
      if (editId) { await api.put(`/admin/testimonials/${editId}`, payload); toast.success("Testimonial updated"); }
      else { await api.post("/admin/testimonials", payload); toast.success("Testimonial added"); }
      reset(); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const edit = (t) => {
    setEditId(t.id);
    setForm({ name: t.name, role: t.role || "", country: t.country || "", quote: t.quote, rating: t.rating || 5, order: t.order || 100 });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    try { await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="admin-testimonials-manager">
      <form onSubmit={submit} className="space-y-3 rounded-xl border border-slate-200 bg-white p-6" data-testid="admin-testimonial-form">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold text-ink">{editId ? "Edit testimonial" : "Add testimonial"}</h3>
          {editId && <button type="button" onClick={reset} className="flex items-center gap-1 text-xs text-slate-500 hover:text-ink"><X size={14} /> Cancel</button>}
        </div>
        <input required placeholder="Client name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="testimonial-name" className={field} />
        <div className="grid grid-cols-2 gap-3">
          <input placeholder="Role / company" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={field} />
          <input placeholder="Country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className={field} />
        </div>
        <textarea required rows={4} placeholder="Quote" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} data-testid="testimonial-quote" className={field} />
        <div className="grid grid-cols-2 gap-3">
          <select value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} className={field}>
            {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} stars</option>)}
          </select>
          <input type="number" placeholder="Order" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className={field} />
        </div>
        <button type="submit" data-testid="testimonial-submit" className="flex items-center gap-2 rounded-md bg-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-ink-800">
          {editId ? <><FloppyDisk size={16} weight="bold" /> Save changes</> : <><Plus size={16} weight="bold" /> Add</>}
        </button>
      </form>
      <div className="space-y-3">
        <h3 className="font-heading text-lg font-semibold text-ink">Testimonials ({items.length})</h3>
        {items.map((t) => (
          <TestimonialCard key={t.id} t={t} onEdit={edit} onDelete={del} />
        ))}
        {items.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-400">No testimonials yet.</p>}
      </div>
    </div>
  );
}
